import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddParkerEquipmentService } from '@core/services/addParkerEquipment.service';
import { ToastService } from '@shared/services/toast.service';
import { Colors, Makes } from '../booking-flow-v1/constants/booking-flow-v1.constant';
import { CountryISO } from 'ngx-intl-tel-input-gg';
import { EMAIL_VALIDATION_PATTERN, MULTILINGUAL_NAME_VALIDATION_PATTERN } from '@shared/constant/shared.constant';
import { ConfirmationService } from 'primeng/api';
import { ParkerService } from '../user-crm/services/parker.service';
import { concatMap, tap, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { autoUnsubWith } from '@shared/services/utility.abstract';
@Component({
  selector: 'app-add-parker-and-equipment-drawer',
  templateUrl: './add-parker-and-equipment-drawer.component.html',
  styleUrls: ['./add-parker-and-equipment-drawer.component.scss'],
})
export class AddParkerAndEquipmentDrawerComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  @Input() groupUserId!: string;
  @Input() bookingId!: string;
  @Input() spotCount!: string;
  @Input() isGateAccessLinkVisible!: boolean;
  assignedSlotList: any;
  visibleSidebar: boolean = true;
  entryForm: FormGroup;
  newEntry: boolean = false;
  make = Makes;
  colors = Colors;
  selectedEquipmentId: any = null;
  filteredLps: any;
  filteredNames: any;
  filteredEmails: any;
  filteredMobiles: any;
  countryISO: CountryISO = CountryISO.UnitedStates;
  selectedDriverId: any = null;
  selectedDriverData: any;
  selectedVehicleData: any;
  allDrivers;
  allDriverVehicle;

  constructor(
    private readonly addParkerEquipmentService: AddParkerEquipmentService,
    private readonly fb: FormBuilder,
    private readonly toastService: ToastService,
    private readonly confirmationService: ConfirmationService,
    private readonly parkerService: ParkerService,
  ) { }

  ngOnInit() {
    this.loadAllDataSequentially();
  }

  loadAllDataSequentially(): void {

this.getDriverList().pipe(
      tap((response: any) => {
        this.allDrivers = response?.data;
        this.filteredEmails = this.allDrivers.filter(
          (driver) =>
            driver?.email !== null &&
            driver?.email !== undefined &&
            driver?.email.trim() !== ''
        );
        this.filteredMobiles = this.allDrivers.filter(
          (driver) =>
            driver?.mobileNumber !== null &&
            driver?.mobileNumber !== undefined &&
            driver?.mobileNumber.trim() !== ''
        );
      }),
      catchError((_error) => {
        this.allDrivers = this.filteredEmails = this.filteredMobiles = [];
        this.newEntry = false;
        return of(null);
      }),
      concatMap(() =>
        this.getAllVehicleList().pipe(
          tap((response: any) => {
            this.allDriverVehicle = response || [];
            this.filteredLps = [...this.allDriverVehicle];
          }),
          catchError((error) => {
            if (error?.error?.message === 'No Vehicle Found. Please try again.') {
              this.allDriverVehicle = [];
            }
            return of(null);
          })
        )
      ),
      concatMap(() =>
        this.getAssignedSlotList().pipe(
          tap((res) => {
            this.assignedSlotList = res?.data;
            this.addEntry();
          }),
          catchError((_error) => {
            this.assignedSlotList = [];
            this.addEntry();
            return of(null);
          })
        )
      )
    )
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: () => {
        
      },
      error: () => {
        
      }
    });
  }

  private getDriverList(): Observable<any> {
    const request = {
      groupUserId: this.groupUserId,
      bookingId: this.bookingId,
      groupUserDriverList: 2,
      search: ''
    };
    return this.parkerService.getGroupUserDriverListByGroupUserId(request);
  }

  private getAllVehicleList(): Observable<any> {
    const req = {
      bookingId: this.bookingId,
      driverId: this.groupUserId,
      search: '',
    };
    return this.addParkerEquipmentService.getUnAssignedVehicleByGroupUserIdAndBookingId(req);
  }

  private getAssignedSlotList(): Observable<any> {
    const req = {
      bookingId: this.bookingId,
    };
    return this.addParkerEquipmentService.getAssignedGroupDriversByBookingId(req);
  }

  filterParkerFields(event: any, searchType?: string): void {
    const query = event.filter?.toLowerCase() || '';
    const filterNonEmpty = (data: any[], key: string) =>
      data.filter((item) => item[key]?.trim() !== '');
    const sharedData = this.allDrivers;
    this.filteredNames = filterNonEmpty(sharedData, 'firstName');
    this.filteredEmails = filterNonEmpty(sharedData, 'email');
    this.filteredMobiles = filterNonEmpty(sharedData, 'mobileNumber');
    if (searchType === 'name') {
      this.filteredNames = this.filteredNames.filter((item) =>
        item.firstName.toLowerCase().includes(query)
      );
    } else if (searchType === 'email') {
      this.filteredEmails = this.filteredEmails.filter((item) =>
        item.email.toLowerCase().includes(query)
      );
    } else if (searchType === 'mobile') {
      this.filteredMobiles = this.filteredMobiles.filter((item) =>
        item.mobileNumber.toLowerCase().includes(query)
      );
    }

    const matched = this.filteredMobiles.find(
      (item: any) => item?.mobileNumber === query
    );
    if (matched) {
      this.selectedDriverId = matched.id;
      this.selectedDriverData = matched;
      this.validationFunction();
      this.entryForm.patchValue({
        name: matched,
        emailAddress: matched
      });
      this.entryForm.get('name')?.disable();
      this.entryForm.get('emailAddress')?.disable();
    }
  }

  getFlagClass(iso: string): string {
    const lowerCaseIso = iso?.toLowerCase();
    return `fi fi-${lowerCaseIso}`;
  }

shareGateAccessLink(entry: any) {
    const req = {
      bookingId: this.bookingId,
      userId: entry.driverId,
      isAll: false

    };
    this.addParkerEquipmentService.shareGateAccessLink(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (res) => {
        if (res) {
          this.toastService.showToastMessage(
            'success',
            'Success',
            res.message
          );
        }
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error.error.message
        );
      },
    });
  }

  DeleteAssignedGroupBookingDriver(event, data) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'Are you sure you want to remove this Parker and Equipment?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const request = {
          id: data.id,
        };
        
        this.addParkerEquipmentService
          .deleteAssignedGroupBookingDriver(request)
          .pipe(autoUnsubWith(this.destroyRef))
          .subscribe({
            next: (response: any) => {
              
              if (response) {
                this.toastService.showToastMessage(
                  'success',
                  'Success',
                  response.message
                );
                this.loadAllDataSequentially();
              }
            },
            error: (error) => { 
              this.toastService.showToastMessage(
                'error',
                'Error',
                error.error.message
              );
            },
          });
      },
    });
  }

  onEmailSelect(res: any) {
    const selected = res.value;
    if (selected) {
      this.selectedDriverId = res.value.id;
      this.selectedDriverData = res.value;
      this.countryISO = res.value?.iso ?? 'us';
      this.validationFunction();
      this.entryForm.patchValue({
        name: res.value.firstName,
        emailAddress: res.value,
        mobile: res.value
      });
      this.entryForm.get('name')?.disable();
    } else {
      this.selectedDriverId = null;
      this.selectedDriverData = [];
      this.validationFunction();
      this.entryForm.patchValue({
        name: '',
        emailAddress: '',
        mobile: ''
      });
      this.entryForm.get('name')?.disable();
    }

  }
  onMobileNumberSelect(res: any) {
    const selected = res.value;
    if (selected) {
      this.selectedDriverId = res.value.id;
      this.selectedDriverData = res.value;
      this.countryISO = res.value?.iso ?? 'us';
      this.validationFunction();
      this.entryForm.patchValue({
        name: res.value.firstName,
        emailAddress: res.value,
        mobile: res.value
      });
      this.entryForm.get('name')?.disable();
    } else {
      this.selectedDriverId = null;
      this.selectedDriverData = [];
      this.entryForm.patchValue({
        name: '',
        emailAddress: '',
        mobile: ''
      });
      this.entryForm.get('name')?.disable();
    }
  }

  onInputChange(_event, fieldName: string) {
    switch (fieldName) {
      case 'name':
        if (this.selectedDriverId !== null) {
          this.entryForm.patchValue({
            emailAddress: '',
            mobile: '',
          });
        }
        break;
      case 'emailAddress':
        if (this.selectedDriverId !== null) {
          this.entryForm.patchValue({
            name: '',
            mobile: '',
          });
        }
        break;
      case 'mobile':
        if (this.selectedDriverId !== null) {
          this.entryForm.patchValue({
            emailAddress: '',
            name: '',
          });
        }
        break;
    }
    this.selectedDriverData = this.selectedDriverId = null;
    this.validationFunction();
    this.entryForm.get('name')?.enable();
    this.entryForm.get('mobile')?.enable();
    this.entryForm.get('emailAddress')?.enable();
  }

  onlicenseNumberChange(_event) {
    if (this.selectedEquipmentId !== null) {
      this.entryForm.patchValue({
        dotNumber: '',
        make: '',
        color: '',
      });
    }
    this.selectedEquipmentId = this.selectedVehicleData = null;
    this.validationFunction();
    this.entryForm.get('dotNumber')?.enable();
    this.entryForm.get('make')?.enable();
    this.entryForm.get('color')?.enable();
  }

  onNameSelect(user: any) {
    this.selectedDriverId = user.id;
    this.selectedDriverData = user;
    this.validationFunction();
    this.entryForm.patchValue({
      name: user,
      emailAddress: user,
      mobile: user.mobileNumber,
    });
    this.entryForm.get('mobile')?.disable();
    this.entryForm.get('emailAddress')?.disable();
  }

  validatePhoneNumber() {
    const phoneControl = this.entryForm.get('mobile')?.value;
    this.filterParkerFields({ query: phoneControl?.number }, 'mobile');
  }

  addEntry(): void {
    if (this.allDrivers) {
      this.newEntry = true;
      this.entryForm = this.fb.group({
        name: [''],
        emailAddress: ['',[Validators.required]],
        mobile: ['',[Validators.required]],
        licenseNumberPlateName: [''],
        dotNumber: [''],
        make: [null],
        color: [null],
      });
      this.entryForm.get('name')?.disable();
      this.entryForm.get('dotNumber')?.disable();
      this.entryForm.get('make')?.disable();
      this.entryForm.get('color')?.disable();
    }
  }

  validationFunction() {
    if (this.selectedDriverId === null) {
      this.entryForm
        .get('name')
        ?.setValidators([
          Validators.required,
          Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN),
          Validators.minLength(3),
          Validators.maxLength(50),
        ]);
      this.entryForm.get('emailAddress')?.setValidators([
        Validators.required,
        Validators.pattern(EMAIL_VALIDATION_PATTERN),
        Validators.minLength(5),
        Validators.maxLength(100),
      ]);
       this.entryForm
        .get('mobile')
        ?.setValidators([
          Validators.required,
        ]);
      this.entryForm.get('emailAddress')?.updateValueAndValidity();
      this.entryForm.get('name')?.updateValueAndValidity();
    } else {
      this.entryForm.get('name')?.setValidators([]);
      this.entryForm.get('emailAddress')?.setValidators([]);
      this.entryForm.get('emailAddress')?.updateValueAndValidity();
      this.entryForm.get('name')?.updateValueAndValidity();
    }

    if (this.selectedEquipmentId === null) {
      this.entryForm
        .get('licenseNumberPlateName')
        ?.setValidators([
          Validators.required,
          Validators.maxLength(15),
          Validators.minLength(5),
          Validators.pattern('^[A-Za-z0-9]+$'),
        ]);
      this.entryForm
        .get('dotNumber')
        ?.setValidators([
          Validators.maxLength(12),
          Validators.minLength(6),
        ]);
      this.entryForm
        .get('make')
        ?.setValidators([
          Validators.required,
        ]);
      this.entryForm
        .get('color')
        ?.setValidators([
          Validators.required,
        ]);
      this.entryForm.get('dotNumber')?.updateValueAndValidity();
      this.entryForm.get('licenseNumberPlateName')?.updateValueAndValidity();
      this.entryForm.get('make')?.updateValueAndValidity();
      this.entryForm.get('color')?.updateValueAndValidity();
    } else {
      this.entryForm.get('make')?.setValidators([]);
      this.entryForm.get('color')?.setValidators([]);
      this.entryForm.get('licenseNumberPlateName')?.setValidators([]);
      this.entryForm.get('dotNumber')?.setValidators([]);
      this.entryForm.get('make')?.updateValueAndValidity();
      this.entryForm.get('color')?.updateValueAndValidity();
      this.entryForm.get('dotNumber')?.updateValueAndValidity();
      this.entryForm.get('licenseNumberPlateName')?.updateValueAndValidity();
    }
  }

  saveEntry(entry: any) {
    this.validationFunction();

    if (this.entryForm.invalid) {
      this.entryForm.markAllAsTouched();
      return;
    }

    this.assignGroupBookingDriver(
      entry,
      this.selectedDriverId,
      this.selectedEquipmentId
    );
  }

  assignGroupBookingDriver(entry: any, driverId: string, equipmetId: string) {
    let driverSource = {
      firstName: '',
      emailAddress: '',
      mobile: '',
      mobileNumber: '',
      dialCode: '',
      countryCode: '',
    };

    let vehicleSource = {
      licenseNumberPlateName: '',
      dotNumber: '',
      make: '',
      color: '',
    };

    if (driverId === null) {
      driverSource.firstName = entry.name;
      driverSource.emailAddress = entry.emailAddress;
      driverSource.mobileNumber = entry.mobile?.number;
      driverSource.dialCode = entry.mobile?.countryCode;
      driverSource.countryCode = entry.mobile?.dialCode;
    } else {
      driverSource.firstName = this.selectedDriverData.firstName;
      driverSource.emailAddress = this.selectedDriverData.email;
      driverSource.mobileNumber = this.selectedDriverData.mobileNumber;
      driverSource.dialCode = this.selectedDriverData.countryCode;
      driverSource.countryCode = this.selectedDriverData.iso;
    }

    if (equipmetId === null) {
      vehicleSource.licenseNumberPlateName = entry.licenseNumberPlateName;
      vehicleSource.dotNumber = entry.dotNumber;
      vehicleSource.make = entry.make;
      vehicleSource.color = entry.color;
    } else {
      vehicleSource.licenseNumberPlateName =
        this.selectedVehicleData.licenseNumberPlate;
      vehicleSource.dotNumber = this.selectedVehicleData.dotNumber;
      vehicleSource.make = this.selectedVehicleData.make;
      vehicleSource.color = this.selectedVehicleData.color;
    }

    const req = {
      bookingId: this.bookingId,
      groupUserDriverId: driverId,
      userVehicleId: equipmetId,
      licencePlateNumber: vehicleSource.licenseNumberPlateName ?? '',
      make: vehicleSource.make ?? '',
      color: vehicleSource.color ?? '',
      dotNumber: vehicleSource.dotNumber ?? '',
      lastName: '',
      email: driverSource.emailAddress ?? '',
      name: driverSource.firstName ?? '',
      mobileNumber: driverSource.mobileNumber ?? '',
      countryCode: driverSource.dialCode ?? '',
      iso: driverSource.countryCode ?? '',
    };
    this.addParkerEquipmentService.assignGroupBookingDriver(req)
    .pipe(autoUnsubWith(this.destroyRef))
    .subscribe({
      next: (response) => {
        
        this.toastService.showToastMessage(
          'success',
          'Success',
          response.message
        );
        this.newEntry = false;
        this.selectedDriverId = this.selectedEquipmentId = null;
        this.loadAllDataSequentially();
      },
      error: (error) => {
        this.toastService.showToastMessage(
          'error',
          'Error',
          error?.error?.message
        );
      },
    });
  }

  filterEquipmentFields(event: any) {
    this.filteredLps = event.value;
  }

  onLpSelect(event: any): void {
    const selectedLp = event.value;
    if (selectedLp) {
      this.selectedEquipmentId = selectedLp.id;
      this.selectedVehicleData = selectedLp;
      this.validationFunction();
      this.entryForm.patchValue({
        licenseNumberPlateName: selectedLp,
        dotNumber: selectedLp.dotNumber ?? '',
        make: selectedLp.make ?? null,
        color: selectedLp.color ?? null
      });
      this.entryForm.get('dotNumber')?.disable();
      this.entryForm.get('make')?.disable();
      this.entryForm.get('color')?.disable();
    } else {
      this.selectedEquipmentId = null;
      this.selectedVehicleData = [];
      this.entryForm.patchValue({
        licenseNumberPlateName: '',
        dotNumber: '',
        make: '',
        color: ''
      });
      this.entryForm.get('dotNumber')?.disable();
      this.entryForm.get('make')?.disable();
      this.entryForm.get('color')?.disable();
    }
  }

  onSidebarAction(displayData: string) {
    document.body.style.overflow = displayData;
  }
}
